/**
 * Carrier sequence ordering utilities — preset sorting and sort metadata extraction.
 * Current plan carriers are always anchored at position 0.
 */

import {
  CarrierSequencePreset,
  type CarrierSortInfo,
} from "../types/carrierSequence.types";
import {
  PlanRoleEnum,
  type Proposal,
  type ProposalPlanGroup,
} from "../types/proposal.types";

import { matchesProposalCoverageType } from "./coverageTypeNormalizer";

/** Calculates total premium for a carrier (employee-tier rate * lives). */
const calculateCarrierTotalPremium = (
  planGroups: ProposalPlanGroup[],
): number => {
  let total = 0;
  for (const group of planGroups) {
    if (group.planRole === PlanRoleEnum.CURRENT) continue;
    for (const plan of group.plans) {
      if (!plan.rateSchedules) continue;
      for (const schedule of plan.rateSchedules) {
        for (const rate of schedule.rates) {
          // Use employee rate as the primary premium indicator
          if (rate.coverageTierCode === "employee") {
            total += (rate.rate ?? 0) * (rate.numberOfLives ?? 0);
          }
        }
      }
    }
  }
  return total;
};

/** Checks whether a carrier has a "current" plan group. */
const carrierHasCurrentPlan = (planGroups: ProposalPlanGroup[]): boolean =>
  planGroups.some(
    (group) =>
      group.planRole === PlanRoleEnum.CURRENT || group.isCurrent === true,
  );

/** Epoch ms a proposal was created, or null when it carries no usable timestamp. */
export const createdAtMs = (createdAt: string): number | null => {
  const parsed = new Date(createdAt).getTime();

  return Number.isNaN(parsed) ? null : parsed;
};

/** The earliest creation time across a carrier's proposals, or null if none are usable. */
export const earliestCreatedAt = (proposals: Proposal[]): number | null =>
  proposals.reduce<number | null>((earliest, proposal) => {
    const created = createdAtMs(proposal.createdAt);
    if (created === null) return earliest;

    return earliest === null ? created : Math.min(earliest, created);
  }, null);

/**
 * Ranks carriers by when they first responded, most recent first. Carriers with no
 * usable timestamp compare equal, so a stable sort leaves them in encounter order
 * after the timestamped ones.
 *
 * Using the *earliest* proposal is what keeps the board stable: a later addition —
 * a materialized analyst column, a requote, an uploaded document — must not promote
 * its carrier past the ones that responded before it.
 */
export const compareFirstResponseDesc = (
  a: number | null,
  b: number | null,
): number => {
  if (a === null || b === null) {
    if (a === b) return 0;

    return a === null ? 1 : -1;
  }

  return b - a;
};

/**
 * The default carrier order for every Present tab and the exported MPX
 * spreadsheet: alphabetical by carrier name, with a current-plan carrier
 * anchored ahead of the rest.
 *
 * The anchor is a hard product rule, not a tiebreak — Chameleon renders the
 * current plan first, and `useCarrierSequence` rejects any reorder that
 * displaces it. Alphabetical applies to everything after it.
 */
export const compareCurrentThenCarrierName = (
  a: CarrierSortInfo,
  b: CarrierSortInfo,
): number => {
  if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;

  return a.carrierName.localeCompare(b.carrierName);
};

/**
 * Extracts sort metadata, one entry per carrier, from proposal data. A carrier may now own
 * several proposals (its placeholder/quote column plus one per uploaded document), so entries
 * are consolidated by carrier id: the `current` flag is OR-ed and the premium is the max across
 * the carrier's proposals (the awaiting-quote placeholder contributes 0) so ordering stays
 * stable and reflects the carrier's real quote.
 */
export const extractCarrierSortInfo = (
  proposals: Proposal[],
  coverageType: string,
): CarrierSortInfo[] => {
  const byCarrier = new Map<string, CarrierSortInfo>();

  for (const proposal of proposals) {
    const coverage = proposal.coverages.find((c) =>
      matchesProposalCoverageType(c.coverageType, coverageType),
    );
    if (!coverage || coverage.planGroups.length === 0) continue;

    const isCurrent = carrierHasCurrentPlan(coverage.planGroups);
    const totalPremium = calculateCarrierTotalPremium(coverage.planGroups);
    const existing = byCarrier.get(proposal.carrierId);

    if (existing) {
      existing.isCurrent = existing.isCurrent || isCurrent;
      existing.totalPremium = Math.max(existing.totalPremium, totalPremium);
    } else {
      byCarrier.set(proposal.carrierId, {
        carrierId: proposal.carrierId,
        carrierName: proposal.carrierName,
        isCurrent,
        totalPremium,
      });
    }
  }

  return [...byCarrier.values()].sort(compareCurrentThenCarrierName);
};

// ---------------------------------------------------------------------------
// Preset sorting
// ---------------------------------------------------------------------------

/** Sorts carriers by comparator, anchoring current-plan carriers at position 0. */
const sortCarriers = (
  carriers: CarrierSortInfo[],
  compareFn: (a: CarrierSortInfo, b: CarrierSortInfo) => number,
): string[] => {
  const current = carriers.filter((c) => c.isCurrent);
  const nonCurrent = carriers.filter((c) => !c.isCurrent).sort(compareFn);
  return [...current, ...nonCurrent].map((c) => c.carrierId);
};

const PRESET_COMPARATORS: Record<
  CarrierSequencePreset,
  (a: CarrierSortInfo, b: CarrierSortInfo) => number
> = {
  [CarrierSequencePreset.CARRIER_NAME_ASC]: (a, b) =>
    a.carrierName.localeCompare(b.carrierName),
  [CarrierSequencePreset.CARRIER_NAME_DESC]: (a, b) =>
    b.carrierName.localeCompare(a.carrierName),
  [CarrierSequencePreset.PREMIUM_LOW_TO_HIGH]: (a, b) =>
    a.totalPremium - b.totalPremium,
  [CarrierSequencePreset.PREMIUM_HIGH_TO_LOW]: (a, b) =>
    b.totalPremium - a.totalPremium,
};

/** Applies a preset sort strategy to carrier sort info data. */
export const applyCarrierSequencePreset = (
  preset: CarrierSequencePreset,
  carriers: CarrierSortInfo[],
): string[] => sortCarriers(carriers, PRESET_COMPARATORS[preset]);

// ---------------------------------------------------------------------------
// Cross-LOC consistent ordering
// ---------------------------------------------------------------------------

/**
 * Projects a canonical (cross-LOC) carrier order onto the carriers actually
 * present for one LOC, so a carrier dragged to a given position on one
 * coverage type appears in the same relative position on any other coverage
 * type it also quotes — without requiring every LOC to carry its own
 * explicit customization.
 *
 * Carriers from `canonicalOrder` that this LOC doesn't have are dropped.
 * Carriers this LOC has that aren't yet in `canonicalOrder` (e.g. a carrier
 * unique to this coverage, or no cross-LOC order has been set yet) are
 * appended afterward in their existing relative order — `carrierIds` is
 * expected to already be sorted (e.g. via extractCarrierSortInfo's default
 * order), so this never discards that fallback ordering, only reprioritizes
 * carriers the canonical order already has an opinion on.
 */
export const projectCarrierOrder = (
  canonicalOrder: string[],
  carrierIds: string[],
): string[] => {
  const presentIds = new Set(carrierIds);
  const known = canonicalOrder.filter((id) => presentIds.has(id));
  const knownIds = new Set(known);
  const unknown = carrierIds.filter((id) => !knownIds.has(id));
  return [...known, ...unknown];
};

// ---------------------------------------------------------------------------
// Global-order merge — replaying a single-LOC drag onto the cross-LOC order
// ---------------------------------------------------------------------------

/**
 * A single carrier's new position, anchored to whichever neighbor survives
 * the move — the carrier it now immediately follows ("after"), or, when it's
 * now first within its own LOC, the carrier it now immediately precedes
 * ("before"). Anchoring to the LOC-successor in that second case (rather than
 * treating "no LOC predecessor" as "no anchor at all") is what keeps the
 * carrier from jumping to the front of every coverage's carriers instead of
 * just past the one carrier it was dragged across.
 */
export interface CarrierMove {
  movedCarrierId: string;
  referenceCarrierId: string | null;
  position: "before" | "after";
}

/**
 * Derives the relative move a drag produced, from the carrier's new position in
 * `newLocOrder` (a single coverage's own carrier order, post-drag). Only the
 * carrier actually picked up needs to be known — everyone else on the LOC just
 * shifted to make room, which is exactly what re-anchoring one carrier captures.
 */
export const deriveCarrierMove = (
  newLocOrder: readonly string[],
  movedCarrierId: string,
): CarrierMove => {
  const index = newLocOrder.indexOf(movedCarrierId);

  if (index === -1) {
    return { movedCarrierId, referenceCarrierId: null, position: "after" };
  }

  if (index > 0) {
    return {
      movedCarrierId,
      referenceCarrierId: newLocOrder[index - 1],
      position: "after",
    };
  }

  // Now first within this LOC — anchor to whatever now follows it *there*
  // instead. Reading that as "no anchor" would place the carrier ahead of
  // every carrier in the RFP rather than just past the one it was dragged
  // across (CD-2402 follow-up).
  return {
    movedCarrierId,
    referenceCarrierId: newLocOrder[index + 1] ?? null,
    position: "before",
  };
};

/**
 * Replays a single carrier's move against a previous order that may span more
 * carriers than the LOC the drag happened on (e.g. the cross-LOC canonical
 * order) — relocating only the moved carrier and leaving every other carrier's
 * relative position untouched. This is what lets a reorder on a coverage whose
 * carriers are a subset of the global list propagate without reshuffling
 * carriers the drag never touched (CD-2402).
 *
 * If the moved carrier's reference carrier isn't present in `previousOrder` —
 * a carrier this LOC has that the canonical order hasn't seen yet, or the
 * moved carrier had no LOC-neighbor at all — the moved carrier is appended at
 * the end rather than silently dropped.
 */
export const applyCarrierMoveToOrder = (
  previousOrder: readonly string[],
  move: CarrierMove,
): string[] => {
  const withoutMoved = previousOrder.filter((id) => id !== move.movedCarrierId);

  if (move.referenceCarrierId === null) {
    return move.position === "before"
      ? [move.movedCarrierId, ...withoutMoved]
      : [...withoutMoved, move.movedCarrierId];
  }

  const anchorIndex = withoutMoved.indexOf(move.referenceCarrierId);
  if (anchorIndex === -1) {
    return [...withoutMoved, move.movedCarrierId];
  }

  const insertAt = move.position === "after" ? anchorIndex + 1 : anchorIndex;
  return [
    ...withoutMoved.slice(0, insertAt),
    move.movedCarrierId,
    ...withoutMoved.slice(insertAt),
  ];
};

/**
 * All distinct carriers across every coverage type in `proposals` (RFP-wide,
 * not filtered to one LOC), alphabetical by name — the same default order
 * Responses, Comps, and Financials already fall back to independently. Used to
 * seed the cross-LOC canonical order the first time an analyst ever drags a
 * carrier, so that first move has a complete order to apply itself to instead
 * of just the dragged-on coverage's own carrier subset.
 */
export const extractAllCarrierIdsAlphabetical = (
  proposals: readonly Proposal[],
): string[] => {
  const nameById = new Map<string, string>();
  for (const proposal of proposals) {
    if (!nameById.has(proposal.carrierId)) {
      nameById.set(proposal.carrierId, proposal.carrierName);
    }
  }
  return [...nameById.entries()]
    .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB))
    .map(([carrierId]) => carrierId);
};
